import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Loader2, IndianRupee, Receipt } from 'lucide-react';

const PAYMENT_MODES = [
  { value: 'neft',           label: 'NEFT / IMPS / RTGS' },
  { value: 'upi',            label: 'UPI (direct)' },
  { value: 'bank_transfer',  label: 'Bank transfer' },
  { value: 'cash',           label: 'Cash' },
  { value: 'cheque',         label: 'Cheque' },
  { value: 'other',          label: 'Other' },
];

const PURPOSES = [
  { value: 'activation', label: 'Activation fee (₹999)' },
  { value: 'final',      label: 'Final / Balance payment' },
  { value: 'full',       label: 'Full payment' },
  { value: 'addon',      label: 'Add-on / Customization charge' },
];

/**
 * Records an off-gateway payment (NEFT / UPI direct / Cash / Cheque) against
 * an SMB lead. Auto-generates a Tax Invoice + Income transaction on submit.
 * Presented as a dialog triggered from the SMB Lead Detail page.
 *
 * When `presetAddon` is passed the dialog opens pre-configured for a
 * specific add-on charge (locks purpose='addon' and pre-fills amount).
 */
export const ManualPaymentDialog = ({ open, onOpenChange, lead, pkg, presetAddon, onSuccess }) => {
  const defaultDate = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: '', purpose: 'activation', payment_mode: 'neft',
    reference: '', bank_account: '', date: defaultDate, notes: '',
  });
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Reset + preload banks when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    const initialPurpose = presetAddon ? 'addon' : 'activation';
    setForm((f) => ({
      ...f,
      amount: presetAddon ? String(presetAddon.amount ?? '') : '',
      purpose: initialPurpose, payment_mode: 'neft',
      reference: '', bank_account: '', date: defaultDate,
      notes: presetAddon ? `Add-on: ${presetAddon.description}` : '',
    }));
    (async () => {
      try {
        const r = await api.get('/transactions/bank-accounts');
        const list = r.data?.data || [];
        setBanks(list);
        if (list.length) setForm((f) => ({ ...f, bank_account: list[0].id }));
      } catch {
        setBanks([]);
      }
    })();
  }, [open, presetAddon, defaultDate]);

  // Auto-fill amount when purpose changes based on the package snapshot.
  // Skipped when a preset addon locks the amount.
  useEffect(() => {
    if (!pkg || presetAddon) return;
    let amt = '';
    if (form.purpose === 'activation') amt = pkg.activation_amount ?? '';
    else if (form.purpose === 'final') amt = pkg.balance_amount ?? '';
    else if (form.purpose === 'full') amt = pkg.total_amount ?? '';
    setForm((f) => ({ ...f, amount: amt !== undefined && amt !== null ? String(amt) : '' }));
  }, [form.purpose, pkg, presetAddon]);

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.bank_account) { toast.error('Pick the bank account that received the money'); return; }
    if (!form.payment_mode) { toast.error('Pick a payment mode'); return; }

    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/smb/leads/${lead.id}/record-manual-payment`, {
        amount: amt,
        purpose: form.purpose,
        payment_mode: form.payment_mode,
        reference: form.reference.trim(),
        bank_account: form.bank_account,
        date: form.date,
        notes: form.notes.trim(),
        ...(presetAddon ? { smb_addon_id: presetAddon.id } : {}),
      });
      setResult(r.data);
      if (r.data?.success) {
        toast.success(
          r.data.invoice_number
            ? `Payment recorded · Invoice ${r.data.invoice_number}`
            : 'Payment recorded — invoice generation pending',
        );
        if (onSuccess) onSuccess(r.data);
      } else {
        toast.error(r.data?.error || 'Failed to record');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setResult({ error: detail });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy) onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="manual-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-emerald-500" />
            Record Manual Payment
          </DialogTitle>
          <DialogDescription>
            Log an off-gateway payment (NEFT / UPI direct / Cash / Cheque). We&apos;ll
            auto-generate a GST tax invoice and email it to the customer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mp-purpose" className="text-xs">Purpose *</Label>
              <Select
                value={form.purpose}
                onValueChange={(v) => setForm({ ...form, purpose: v })}
                disabled={busy || !!presetAddon}
              >
                <SelectTrigger id="mp-purpose" data-testid="mp-purpose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {presetAddon && (
                <div className="text-[10px] text-muted-foreground">
                  Locked to add-on: <strong className="text-emerald-400">{presetAddon.description}</strong>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mp-amount" className="text-xs">Amount (₹) *</Label>
              <Input
                id="mp-amount"
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                disabled={busy}
                data-testid="mp-amount"
                placeholder="e.g. 999"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mp-mode" className="text-xs">Payment mode *</Label>
              <Select
                value={form.payment_mode}
                onValueChange={(v) => setForm({ ...form, payment_mode: v })}
                disabled={busy}
              >
                <SelectTrigger id="mp-mode" data-testid="mp-payment-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mp-date" className="text-xs">Payment date *</Label>
              <Input
                id="mp-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                disabled={busy}
                data-testid="mp-date"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp-bank" className="text-xs">Received in bank account *</Label>
            <Select
              value={form.bank_account}
              onValueChange={(v) => setForm({ ...form, bank_account: v })}
              disabled={busy || banks.length === 0}
            >
              <SelectTrigger id="mp-bank" data-testid="mp-bank-account">
                <SelectValue placeholder={banks.length ? 'Select…' : 'No bank accounts configured'} />
              </SelectTrigger>
              <SelectContent>
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name || `${b.bank} — ${b.account_number}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp-ref" className="text-xs">Reference (UTR / IMPS / cheque no.)</Label>
            <Input
              id="mp-ref"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              disabled={busy}
              placeholder="e.g. IMPS-A123456 or CHQ-000123"
              data-testid="mp-reference"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp-notes" className="text-xs">Notes (optional)</Label>
            <Textarea
              id="mp-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              disabled={busy}
              rows={2}
              placeholder="Any additional context for accounts team"
              data-testid="mp-notes"
            />
          </div>

          {result?.invoice_number && (
            <div
              className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs space-y-1"
              data-testid="mp-result"
            >
              <div className="flex items-center gap-1.5 font-semibold text-emerald-500">
                <Receipt className="h-3.5 w-3.5" />
                Tax invoice generated
              </div>
              <div><span className="text-muted-foreground">Invoice:</span> <span className="font-mono">{result.invoice_number}</span></div>
              {result.transaction_id && (
                <div><span className="text-muted-foreground">Transaction:</span> <span className="font-mono">{result.transaction_id.slice(0, 8)}…</span></div>
              )}
              <div>
                <span className="text-muted-foreground">Email:</span>{' '}
                <span className={result.email_status === 'sent' ? 'text-emerald-400' : 'text-amber-400'}>
                  {result.email_status}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">PDF:</span>{' '}
                <span className={result.pdf_ok ? 'text-emerald-400' : 'text-red-400'}>
                  {result.pdf_ok ? '✓ Generated' : '✗ Failed (see logs)'}
                </span>
              </div>
            </div>
          )}
          {result?.error && (
            <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-400" data-testid="mp-error">
              {result.error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy} data-testid="mp-cancel">
            {result?.success ? 'Close' : 'Cancel'}
          </Button>
          {!result?.success && (
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="mp-submit"
            >
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Recording…</> : 'Record & Generate Invoice'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPaymentDialog;
